import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Github, Linkedin, Mail, Phone, Menu, X, Sun, Moon, Download, ExternalLink } from "lucide-react";
import Photo from "./assets/yuvan.jpg";     
// Updated color scheme - more distinctive teal accent
const colors = {
  primary: "bg-teal-600 hover:bg-teal-700",
  primaryText: "text-teal-600 dark:text-teal-400",
  primaryLight: "bg-teal-100 dark:bg-teal-900/30",
};

// Skill logos mapping
const skillLogos = {
  "Python": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
  "Flask": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flask/flask-original.svg",
  "Deep Learning": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg",
  "SQL": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg",
  "HTML/CSS": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg",
  "React Bootstrap": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bootstrap/bootstrap-original.svg",
  "Figma": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg",
  "Machine Learning": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytorch/pytorch-original.svg",
  "Communication": "https://img.icons8.com/fluency/48/communication.png",
  "Critical Thinking": "https://cdn-icons-png.flaticon.com/512/9631/9631082.png",
  "Time Management": "https://img.icons8.com/fluency/48/time-management.png",
  "Git": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg"
};

// Skill colors for hover effect - updated to teal theme
const skillColors = {
  "Python": "hover:bg-teal-50 hover:text-teal-800 dark:hover:bg-teal-900/20 dark:hover:text-teal-200",
  "Flask": "hover:bg-teal-50 hover:text-teal-800 dark:hover:bg-teal-900/20 dark:hover:text-teal-200",
  "Deep Learning": "hover:bg-teal-50 hover:text-teal-800 dark:hover:bg-teal-900/20 dark:hover:text-teal-200",
  "SQL": "hover:bg-teal-50 hover:text-teal-800 dark:hover:bg-teal-900/20 dark:hover:text-teal-200",
  "HTML/CSS": "hover:bg-teal-50 hover:text-teal-800 dark:hover:bg-teal-900/20 dark:hover:text-teal-200",
  "React Bootstrap": "hover:bg-teal-50 hover:text-teal-800 dark:hover:bg-teal-900/20 dark:hover:text-teal-200",
  "Figma": "hover:bg-teal-50 hover:text-teal-800 dark:hover:bg-teal-900/20 dark:hover:text-teal-200",
  "Machine Learning": "hover:bg-teal-50 hover:text-teal-800 dark:hover:bg-teal-900/20 dark:hover:text-teal-200",
  "Communication": "hover:bg-teal-50 hover:text-teal-800 dark:hover:bg-teal-900/20 dark:hover:text-teal-200",
  "Critical Thinking": "hover:bg-teal-50 hover:text-teal-800 dark:hover:bg-teal-900/20 dark:hover:text-teal-200",
  "Time Management": "hover:bg-teal-50 hover:text-teal-800 dark:hover:bg-teal-900/20 dark:hover:text-teal-200"
};

function Navbar({ darkMode, setDarkMode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 w-full transition-all duration-300 z-50 ${scrolled ? "bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm shadow-md" : "bg-transparent"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
        <h1 className={`text-xl font-bold ${colors.primaryText}`}>
          Yuvan Velkumar
        </h1>
        <div className="hidden md:flex space-x-6 items-center">
          <a href="#about" className="text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">About</a>
          <a href="#education" className="text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Education</a>
          <a href="#experience" className="text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Experience</a>
          <a href="#projects" className="text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Projects</a>
          <a href="#skills" className="text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Skills</a>
          <a href="#certifications" className="text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Certifications</a>
          <a href="#contact" className="text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Contact</a>
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Toggle dark mode"
          >
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          onClick={() => setMenuOpen(!menuOpen)} 
          className="md:hidden text-gray-700 dark:text-gray-300"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Dropdown */}
      {menuOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-white dark:bg-gray-900 px-6 pb-4 space-y-3 shadow-lg"
        >
          <a href="#about" className="block py-2 text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">About</a>
          <a href="#education" className="block py-2 text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Education</a>
          <a href="#experience" className="block py-2 text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Experience</a>
          <a href="#projects" className="block py-2 text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Projects</a>
          <a href="#skills" className="block py-2 text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Skills</a>
          <a href="#certifications" className="block py-2 text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Certifications</a>
          <a href="#contact" className="block py-2 text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors">Contact</a>
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center"
          >
            {darkMode ? <Sun className="w-5 h-5 mr-2" /> : <Moon className="w-5 h-5 mr-2" />}
            {darkMode ? "Light Mode" : "Dark Mode"}
          </button>
        </motion.div>
      )}
    </nav>
  );
}

function Hero() {
  return (
    <section className="min-h-screen flex flex-col justify-center items-center text-center px-4 pt-20 pb-16 bg-gradient-to-br from-teal-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-cyan-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
      </div>
      
      <div className="relative z-10 max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8 mx-auto w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-4 border-white dark:border-gray-800 shadow-lg"
        >
          {/* Replace with your actual image path */}
          <img 
            src={Photo}
            alt="Yuvan Velkumar" 
            className="w-full h-full object-cover"
          />
        </motion.div>
        
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 dark:text-gray-100 mb-4"
        >
          Hi, I'm <span className={colors.primaryText}>Yuvan Velkumar</span>
        </motion.h1>
        
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-xl mx-auto"
        >
          AI & Data Science Enthusiast | ML & Flask Developer
        </motion.p>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <div className="flex flex-wrap justify-center gap-3">
            <a href="https://github.com/yuvan-s-96" target="_blank" rel="noreferrer" className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-md text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:shadow-lg transition-all">
              <Github className="w-5 h-5 md:w-6 md:h-6"/>
            </a>
            <a href="https://www.linkedin.com/in/yuvan-velkumar-761700249" target="_blank" rel="noreferrer" className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-md text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:shadow-lg transition-all">
              <Linkedin className="w-5 h-5 md:w-6 md:h-6"/>
            </a>
            <a href="mailto:yuvanvelkumars@gmail.com" className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-md text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:shadow-lg transition-all">
              <Mail className="w-5 h-5 md:w-6 md:h-6"/>
            </a>
            <a href="tel:+917904250939" className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-md text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:shadow-lg transition-all">
              <Phone className="w-5 h-5 md:w-6 md:h-6"/>
            </a>
          </div>
          
          <a 
            href="https://drive.google.com/drive/folders/1RtlpD07pUpTGhdmWDtWdnw6LmSwqzoA4?usp=sharing" 
            download 
            className={`flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 ${colors.primary} text-white rounded-full shadow-md hover:shadow-lg transition-all text-sm md:text-base`}
          >
            <Download className="w-4 h-4 md:w-5 md:h-5" />
            Download Resume
          </a>
        </motion.div>
      </div>
      
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce"
      >
        <div className="w-5 h-8 md:w-6 md:h-10 border-2 border-gray-400 dark:border-gray-600 rounded-full flex justify-center">
          <div className="w-1 h-2 md:h-3 bg-gray-400 dark:bg-gray-600 rounded-full mt-2"></div>
        </div>
      </motion.div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="py-16 max-w-5xl mx-auto px-4 sm:px-6">
      <motion.h2 
        initial={{opacity:0, y:20}} 
        whileInView={{opacity:1, y:0}} 
        className="text-2xl md:text-3xl font-bold mb-6 text-gray-800 dark:text-gray-100"
      >
        About Me
      </motion.h2>
      <motion.p 
        initial={{opacity:0}} 
        whileInView={{opacity:1}} 
        transition={{delay:0.2}} 
        className="text-gray-700 dark:text-gray-300 leading-relaxed text-base md:text-lg"
      >
        Motivated individual with a background in computer science, passionate about technology and dedicated to continuous learning in software development, data science, and artificial intelligence. Seeking opportunities to leverage expertise in AI and data science to contribute to forward-thinking organizations.
      </motion.p>
    </section>
  );
}

function Skills() {
  const skills = ["Python", "Flask","Deep Learning" ,"SQL", "HTML/CSS", "React Bootstrap", "Figma", "Machine Learning", "Git", "Communication", "Critical Thinking", "Time Management"];
  return (
    <section id="skills" className="py-16 bg-gray-50 dark:bg-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-gray-800 dark:text-gray-100">Skills</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
          {skills.map((skill, i) => (
            <motion.div 
              whileHover={{scale:1.05}} 
              whileInView={{opacity:1, y:0}} 
              initial={{opacity:0, y:20}} 
              transition={{duration:0.3, delay:i*0.05}} 
              key={i} 
              className={`p-3 md:p-4 bg-white dark:bg-gray-900 rounded-xl shadow text-center text-gray-800 dark:text-gray-200 font-medium hover:shadow-md transition-all flex flex-col items-center ${skillColors[skill]}`}
            >
              {skillLogos[skill] && (
                <img 
                  src={skillLogos[skill]} 
                  alt={skill} 
                  className="w-8 h-8 md:w-10 md:h-10 mb-2 object-contain"
                />
              )}
              <span className="text-xs md:text-sm">{skill}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Experience() {
  const jobs = [
    {
      title:"Deep Learning Intern - Tech Jose", 
      period:"Apr 2025 - Jun 2025", 
      desc:"Explored the fundamentals of deep learning and gradually advanced to real-world applications.",
      skills: ["Python", "TensorFlow", "Neural Networks"]
    },
    {
      title:"Intern - Bejawar Innovative Technologies", 
      period:"Jun 2023 - Feb 2024", 
      desc:"Worked on LangChain, vector DB, intent classification, and Flask.",
      skills: ["Python", "Flask", "LangChain", "Vector Databases"]
    },
    {
      title:"Data Science Intern - Lets Grow More", 
      period:"Sep 2023 - Oct 2023", 
      desc:"Data cleaning, feature extraction, and ML model training.",
      skills: ["Python", "Pandas", "Scikit-learn", "Data Visualization"]
    },
    {
      title:"Intern - Cisco", 
      period:"May 2023 - Jun 2023", 
      desc:"Basics of networking and Packet Tracer.",
      skills: ["Networking", "Cisco", "Packet Tracer"]
    },
    {
      title:"Summer Intern - Aroganam Business Consultancy", 
      period:"Jul 2020 - Aug 2020", 
      desc:"Frontend apps with React Bootstrap.",
      skills: ["React", "Bootstrap", "JavaScript"]
    },
    {
      title:"Intern - Edunet Foundation", 
      period:"Sep 2020 - Nov 2020", 
      desc:"Computer vision project: traffic sign recognition.",
      skills: ["Python", "OpenCV", "Computer Vision"]
    }
  ];
  return (
    <section id="experience" className="py-16 max-w-5xl mx-auto px-4 sm:px-6">
      <h2 className="text-2xl md:text-3xl font-bold mb-8 text-gray-800 dark:text-gray-100">Experience</h2>
      <div className="space-y-4 md:space-y-6">
        {jobs.map((job,i)=>(
          <motion.div 
            key={i} 
            initial={{opacity:0, y:20}} 
            whileInView={{opacity:1, y:0}} 
            transition={{delay:i*0.1}} 
            className="p-4 md:p-6 bg-white dark:bg-gray-900 rounded-xl shadow hover:shadow-lg transition-shadow"
          >
            <h3 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100">{job.title}</h3>
            <p className="text-xs md:text-sm text-gray-500 mb-2">{job.period}</p>
            <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 mb-3">{job.desc}</p>
            <div className="flex flex-wrap gap-2">
              {job.skills.map((skill, idx) => (
                <span key={idx} className="text-xs px-2 py-1 bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-200 rounded-full">
                  {skill}
                </span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Projects() {
  const projects = [
    {
      name:"IPL Score & Player Value Prediction", 
      desc:"ML prediction model with HTML/CSS UI that forecasts IPL match outcomes and player valuations based on historical performance data.",
      skills: ["Python", "Machine Learning", "Flask", "HTML/CSS"],
      link: "#"
    },
    {
      name:"Traffic Sign Recognition", 
      desc:"Computer vision system using OpenCV and deep learning to detect and classify traffic signs in real-time with 92% accuracy.",
      skills: ["Python", "OpenCV", "CNN", ],
      link: "#"
    },
    {
      name:"Contact Centre Knowledge Management Tool", 
      desc:"Smart India Hackathon 2023 finals selected solution for managing knowledge base with intelligent search and NLP capabilities for customer service teams.",
      skills: ["Python",  "JavaScript"],
      link: "#"
    },
    {
      name:"Deep Fake Detection", 
      desc:"Final year project implementing multi-modal detection of deep fakes in audio, video, and images using ensemble learning approaches.",
      skills: ["Python", "Deep Learning", "PyTorch","HuggingFace"],
      link: "#"
    }
  ];
  return (
    <section id="projects" className="py-16 bg-gray-50 dark:bg-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-gray-800 dark:text-gray-100">Key Projects</h2>
        <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
          {projects.map((p,i)=>(
            <motion.div 
              key={i} 
              initial={{opacity:0, y:20}} 
              whileInView={{opacity:1, y:0}} 
              transition={{delay:i*0.1}} 
              className="p-4 md:p-6 bg-white dark:bg-gray-900 rounded-xl shadow hover:shadow-lg transition-shadow group"
            >
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                  {p.name}
                </h3>
                {/* {p.link && (
                  <a href={p.link} className="text-gray-400 hover:text-teal-600 transition-colors">
                    <ExternalLink size={16} />
                  </a>
                )} */}
              </div>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 mb-4">{p.desc}</p>
              <div className="flex flex-wrap gap-2">
                {p.skills.map((skill, idx) => (
                  <span key={idx} className="text-xs px-2 py-1 bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-200 rounded-full">
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GithubProjects() {
  const [repos, setRepos] = useState([]);
  const [visibleCount, setVisibleCount] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchRepos() {
      try {
        setLoading(true);
        setError(null);
        
        // First try the Netlify function
        let response = await fetch('/.netlify/functions/github-repos');
        
        // If the function doesn't exist (404), try direct API (with limited access)
        if (response.status === 404) {
          console.log('Netlify function not found, trying direct API');
          response = await fetch(
            'https://api.github.com/users/yuvan-s-96/repos?sort=updated&per_page=10'
          );
        }
        
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Handle GitHub API error messages
        if (data.message && data.message.includes('API rate limit')) {
          throw new Error('GitHub API rate limit exceeded. Please try again later.');
        }
        
        if (!Array.isArray(data)) {
          throw new Error('Invalid response from server');
        }
        
        // If we have a description, use it, otherwise create a placeholder
        const reposWithDetails = data.map(repo => ({
          ...repo,
          finalDescription: repo.description || 'No description available'
        }));
        
        setRepos(reposWithDetails);
      } catch (err) {
        console.error('Failed to fetch repos:', err);
        setError(err.message);
        setRepos([]);
      } finally {
        setLoading(false);
      }
    }

    fetchRepos();
  }, []);

  if (loading) {
    return (
      <section className="py-16 max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-gray-800 dark:text-gray-100">GitHub Projects</h2>
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-gray-800 dark:text-gray-100">GitHub Projects</h2>
        <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-lg">
          <h3 className="text-red-800 dark:text-red-200 font-medium">Error Loading Projects</h3>
          <p className="text-red-700 dark:text-red-300 mt-2">{error}</p>
          <p className="text-red-600 dark:text-red-400 text-sm mt-4">
            This could be due to GitHub API rate limiting. The data will load automatically when available.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 max-w-6xl mx-auto px-4 sm:px-6">
      <h2 className="text-2xl md:text-3xl font-bold mb-8 text-gray-800 dark:text-gray-100">GitHub Projects</h2>
      
      {repos.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>
            </svg>
          </div>
          <p className="text-gray-500 dark:text-gray-400">No repositories found</p>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {repos.slice(0, visibleCount).map((repo) => (
              <motion.a
                key={repo.id}
                href={repo.html_url}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="p-4 md:p-6 bg-white dark:bg-gray-800 rounded-xl shadow hover:shadow-lg transition-shadow block group border border-gray-200 dark:border-gray-700"
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-gray-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                    {repo.name}
                  </h3>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-teal-600 transition-colors mt-1 flex-shrink-0" />
                </div>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                  {repo.finalDescription}
                </p>
                <div className="flex space-x-4 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                  <span>⭐ {repo.stargazers_count}</span>
                  <span>🍴 {repo.forks_count}</span>
                  <span>{repo.language || 'Code'}</span>
                </div>
              </motion.a>
            ))}
          </div>
          
          {visibleCount < repos.length && (
            <div className="text-center mt-8">
              <button
                onClick={() => setVisibleCount(visibleCount + 3)}
                className="px-5 py-2.5 md:px-6 md:py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all text-sm md:text-base"
              >
                Load More
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
function Education() {
  const schools = [
    {title:"B.Tech Artificial Intelligence & Data Science", place:"KGiSL Institute of Technology", detail:"CGPA: 8.5 / 10.0 (2021 - 2025)"},
    {title:"Higher Secondary", place:"Bharatiya Vidya Bhavan's Gandhi Vidyashram", detail:"94.8% (2021)"}
  ];
  return (
    <section id="education" className="py-16 bg-gray-50 dark:bg-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-gray-800 dark:text-gray-100">Education</h2>
        <div className="space-y-4 md:space-y-6">
          {schools.map((s,i)=>(
            <motion.div 
              key={i} 
              initial={{opacity:0, y:20}} 
              whileInView={{opacity:1, y:0}} 
              className="p-4 md:p-6 bg-white dark:bg-gray-900 rounded-xl shadow hover:shadow-lg transition-shadow"
            >
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100">{s.title}</h3>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">{s.place}</p>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{s.detail}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="py-16 max-w-4xl mx-auto px-4 sm:px-6 text-center">
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800 dark:text-gray-100">Contact</h2>
      <p className="text-gray-700 dark:text-gray-300 mb-6 text-base md:text-lg">Let's connect for opportunities or collaborations!</p>
      <div className="flex flex-wrap justify-center gap-4 md:gap-6">
        <a href="mailto:yuvanvelkumars@gmail.com" className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-md text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:shadow-lg transition-all">
          <Mail className="w-5 h-5 md:w-6 md:h-6"/>
        </a>
        <a href="https://github.com/yuvan-s-96" target="_blank" rel="noreferrer" className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-md text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:shadow-lg transition-all">
          <Github className="w-5 h-5 md:w-6 md:h-6"/>
        </a>
        <a href="https://www.linkedin.com/in/yuvan-velkumar-761700249" target="_blank" rel="noreferrer" className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-md text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:shadow-lg transition-all">
          <Linkedin className="w-5 h-5 md:w-6 md:h-6"/>
        </a>
        <a href="tel:+917904250939" className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-md text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:shadow-lg transition-all">
          <Phone className="w-5 h-5 md:w-6 md:h-6"/>
        </a>
      </div>
    </section>
  );
}

function Certifications() {
  const certs = [
    { name:"Accenture North America - Data Analytics and Visualization Job Simulation", issuer: "Forage", year: "2024" },
    { name: "Deep Learning and Reinforcement Learning", issuer: "Coursera", year: "2023" },
    { name: "Git for Developers Using Github", issuer: "Coursera", year: "2024" },
    { name: "Data Visualization with Python", issuer: "Coursera", year: "2022" },
    { name: "Databases and SQL for Data Science", issuer: "Coursera", year: "2022" },
    { name: "AWS Academy Graduate - AWS Academy Cloud Foundations", issuer: "AWS Academy", year: "2023" },
  ];

  return (
    <section id="certifications" className="py-16 bg-gray-50 dark:bg-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-gray-800 dark:text-gray-100">Certifications</h2>
        <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
          {certs.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="p-4 md:p-6 bg-white dark:bg-gray-900 rounded-xl shadow hover:shadow-lg transition-shadow"
            >
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100">{c.name}</h3>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">{c.issuer}</p>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{c.year}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-6 bg-gray-100 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          © {new Date().getFullYear()} Yuvan Velkumar. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

export default function App() {
  const [darkMode, setDarkMode] = useState(true);

  // Add the animation styles to the document head
  useEffect(() => {
    const styleSheet = document.createElement("style")
    styleSheet.innerText = `
      @keyframes blob {
        0% { transform: translate(0px, 0px) scale(1); }
        33% { transform: translate(30px, -50px) scale(1.1); }
        66% { transform: translate(-20px, 20px) scale(0.9); }
        100% { transform: translate(0px, 0px) scale(1); }
      }
      .animate-blob {
        animation: blob 7s infinite;
      }
      .animation-delay-2000 {
        animation-delay: 2s;
      }
      .animation-delay-4000 {
        animation-delay: 4s;
      }
    `
    document.head.appendChild(styleSheet)
    return () => document.head.removeChild(styleSheet)
  }, [])

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="font-sans text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900">
        <Navbar darkMode={darkMode} setDarkMode={setDarkMode} />
        <Hero />
        <About />
        <Education />
        <Experience />
        <Projects />
        <GithubProjects />
        <Skills />
        <Certifications/>
        <Contact />
        <Footer />
      </div>
    </div>
  );
}
